import { useState } from "react";
import { Copy, Check, Lock, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function AdminPage() {
  const [copied, setCopied] = useState(false);
  const origin =
    process.env.REACT_APP_BACKEND_URL ||
    (typeof window !== "undefined" ? window.location.origin : "");
  const embedUrl = `${origin}/embed`;

  const code = `<iframe
  src="${embedUrl}"
  width="100%"
  height="780"
  style="border:0;max-width:760px;display:block;margin:0 auto;background:#08080b;border-radius:16px"
  loading="lazy"
  title="IF Detector - Detector de video IA"
  allow="clipboard-read; clipboard-write"
></iframe>`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Código copiado");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div
      className="min-h-screen vx-glow-bg text-white"
      data-testid="admin-page"
    >
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
        <a
          href="/"
          className="inline-flex items-center gap-2 text-xs text-[--vx-text-dim] hover:text-white font-mono uppercase tracking-wider"
        >
          ← Volver
        </a>

        <div className="mt-6 inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[--vx-border-strong] bg-[--vx-surface] text-xs font-mono uppercase tracking-wider text-[--vx-text-dim]">
          <Lock className="w-3.5 h-3.5" />
          Panel privado
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-4">
          Embed para tu WordPress
        </h1>
        <p className="text-base text-[--vx-text-dim] mt-3 max-w-xl">
          Esta página es solo para ti. Copia el código del iframe y pégalo en cualquier
          página de tu WordPress (bloque <span className="font-mono text-white">HTML personalizado</span>).
          Tus usuarios verán la herramienta IF Detector pero <strong>no</strong> tendrán acceso a este código.
        </p>

        {/* Steps */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-8">
          {[
            ["01", "Copia el código", "Pulsa el botón Copiar."],
            ["02", "Bloque HTML", "En WordPress añade un bloque HTML personalizado."],
            ["03", "Publica", "Guarda y previsualiza. IF Detector aparecerá embebido."],
          ].map(([n, t, d]) => (
            <div key={n} className="vx-card rounded-2xl p-5">
              <div className="text-xs font-mono text-[--vx-text-faint]">{n}</div>
              <div className="font-display font-semibold mt-2">{t}</div>
              <p className="text-sm text-[--vx-text-dim] mt-1.5 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>

        {/* Code */}
        <div className="mt-8 vx-card rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[--vx-border] flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.2em] text-[--vx-text-dim] font-mono">
              Código iframe
            </div>
            <Button
              data-testid="copy-embed-btn"
              onClick={copy}
              className="h-8 px-3 rounded-lg bg-white text-black hover:bg-white/90 font-semibold text-xs"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 mr-1.5" />
                  Copiado
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5 mr-1.5" />
                  Copiar
                </>
              )}
            </Button>
          </div>
          <pre
            className="font-mono text-xs sm:text-sm text-white p-5 overflow-x-auto leading-relaxed bg-[--vx-bg]"
            data-testid="embed-code"
          >
            <code>{code}</code>
          </pre>
        </div>

        <div className="mt-6 vx-card rounded-2xl p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-[--vx-text-dim] font-mono">
            URL del embed
          </div>
          <a
            href={embedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-sm break-all text-white hover:underline inline-flex items-center gap-2 mt-2"
            data-testid="embed-url-link"
          >
            {embedUrl}
            <ExternalLink className="w-3.5 h-3.5 shrink-0" />
          </a>
          <p className="text-xs text-[--vx-text-dim] mt-3 leading-relaxed">
            Mantén esta ruta <span className="font-mono">/admin</span> en privado.
            No está enlazada desde la página pública.
          </p>
        </div>
      </div>
    </div>
  );
}
